import {
	type MouseEvent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	useFirePostOfficeTrigger,
	useTrigger,
} from '@atlassian/post-office-context/post-office-trigger';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
	UFOExperience,
} from '@atlassian/ufo/ufo-experimental';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { ConfluenceExperimentsContext } from '@post-office/growth-common/confluence-experiments-context';
import { CANONICAL_TEMPLATE_KEYS } from '@post-office/growth-common/confluence-experiments-context/template-xflow';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { type MessageTemplateContext } from '@post-office/message-template--twc-sidenav-banner';
import { type WaitForTriggerFunction } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import { TrackMount } from '@post-office/placement-contexts/track-mount';
import {
	type PlacementErrorBoundaryProps,
	withErrorBoundary,
} from '@post-office/placement-contexts/withErrorBoundary';
import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
} from '@post-office/placement-common-v2';
import type {
	CreatePlacementMessageConfig,
	AnyPayload,
} from '@post-office/message-template-contracts/placement-v2';
import {
	type BackendSyncMessageStage,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { TEMPLATE_XFLOW_EXPERIMENT_KEY } from '@post-office/placements--growth/confluence-side-nav/contexts/post-office-side-nav-message-provider';
import {
	type TransformMetadataResult,
	TeamworkCollectionCcpId,
} from '@post-office/shared-contracts/message-template-metadata';
import { TapProvider } from '@post-office/util-tap-traits';

import { PersonalizedBanner } from '../../../components/personalized-banner';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import {
	TEMPLATE_XFLOW_HYDRATION_FAILURE_METRIC,
	TEMPLATE_XFLOW_HYDRATION_FAILURE_REASONS,
	TEMPLATE_XFLOW_MESSAGE_TEMPLATE_ID,
	TEMPLATE_XFLOW_PLACEMENT_ID,
	TEMPLATE_XFLOW_RENDER_ABORT_REASONS,
	TEMPLATE_XFLOW_RENDER_EXPERIENCE,
	TEMPLATE_XFLOW_RENDER_FAILURE_REASON,
} from '../../../template-xflow-delivery-health';
import { getLoomOnboardingUrl } from '../../../utils/get-onboarding-url';
import { getTemplateXflowReminderTapTrait } from './getTemplateXflowReminderTapTrait';

const TEMPLATE_XFLOW_GATE = 'twcg-2112-twc-xflow-conf-pages-templates-gate';
const TEMPLATE_VIEW_TRIGGER_ID = 'twcg-2112-template-view-trigger';
const TEMPLATE_APPLIED_TRIGGER_ID = 'twcg-2112-template-applied-trigger';
const TEMPLATE_XFLOW_DELAY_MS = 3_000;

const templateXflowMessages = defineMessages({
	meetingNotesHeading: {
		id: 'twc.sidenav.banner.template.xflow.meeting.notes.heading',
		defaultMessage: 'Automate meeting notes',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Meeting Notes template.',
	},
	projectPlanHeading: {
		id: 'twc.sidenav.banner.template.xflow.project.plan.heading',
		defaultMessage: 'Instantly create a project plan',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Project Plan template.',
	},
	eowStatusReportHeading: {
		id: 'twc.sidenav.banner.template.xflow.eow.status.report.heading',
		defaultMessage: 'Instantly create reports',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the End of Week Status Report template.',
	},
	decisionsHeading: {
		id: 'twc.sidenav.banner.template.xflow.decisions.heading',
		defaultMessage: 'Instantly capture decisions',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Decisions template.',
	},
	requirementsRaciHeading: {
		id: 'twc.sidenav.banner.template.xflow.requirements.raci.heading',
		defaultMessage: 'Instantly capture requirements',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Requirements RACI template.',
	},
	genericHeading: {
		id: 'twc.sidenav.banner.template.xflow.generic.heading',
		defaultMessage: 'Don’t start from scratch',
		description:
			'Heading for the Loom cross-flow banner on a supported Confluence template without dedicated copy.',
	},
	tryItFree: {
		id: 'twc.sidenav.banner.template.xflow.try.it.free',
		defaultMessage: 'Try it free',
		description: 'CTA text for opening the Teamwork Collection cross-flow modal.',
	},
	remindMeLater: {
		id: 'twc.sidenav.banner.template.xflow.remind.me.later',
		defaultMessage: 'Remind me later',
		description: 'Action text for snoozing the Teamwork Collection cross-flow banner.',
	},
});

const templateXflowHeadingByBlueprintModuleCompleteKey: Readonly<
	Record<string, MessageDescriptor>
> = {
	[CANONICAL_TEMPLATE_KEYS.MEETING_NOTES]: templateXflowMessages.meetingNotesHeading,
	[CANONICAL_TEMPLATE_KEYS.PROJECT_PLAN]: templateXflowMessages.projectPlanHeading,
	[CANONICAL_TEMPLATE_KEYS.EOW_STATUS_REPORT]: templateXflowMessages.eowStatusReportHeading,
	[CANONICAL_TEMPLATE_KEYS.DECISIONS]: templateXflowMessages.decisionsHeading,
	[CANONICAL_TEMPLATE_KEYS.REQUIREMENTS_RACI]: templateXflowMessages.requirementsRaciHeading,
	[CANONICAL_TEMPLATE_KEYS.RETROSPECTIVES]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.BUSINESS_STATUS_UPDATE]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.ONE_ON_ONE_MEETING]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.RECURRING_MEETING_MINUTES]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.PROJECT_STATUS]: templateXflowMessages.genericHeading,
};

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onboardingUrl: string;
	messageInstanceId: string;
	locale: string;
	redirectTo: 'jira' | 'confluence';
	experimentKey?: string;
	targetTenantId?: string;
	collectionEntitlementId?: string;
	waitForTrigger?: WaitForTriggerFunction;
};

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation(),
		stage: () => ({
			rerankingMetadata: { productId: TeamworkCollectionCcpId },
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: async ({ message, request }) => {
			const { logger, orchestrationContext } = request;
			const { messageInstanceId, messageTemplateId, triggerId } = message;
			const { cloudId: tenantId, experimentKey, explicitEnableRequired } = message.context;
			const isTemplateXflowMessage =
				experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY && explicitEnableRequired === true;
			const { locale } = orchestrationContext;

			// The template-xflow filter prevents legacy messages from reaching hydration.
			// Preserve a quiet no-op fallback in case a caller bypasses filters.
			if (!isTemplateXflowMessage) {
				return message.context;
			}

			const { targetTenantId, collectionEntitlementId } = message.context;

			if (!tenantId || typeof tenantId !== 'string') {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Tenant ID not available',
				);
				request.metrics
					.counter(TEMPLATE_XFLOW_HYDRATION_FAILURE_METRIC, {
						reason: TEMPLATE_XFLOW_HYDRATION_FAILURE_REASONS.TENANT_ID_NOT_AVAILABLE,
					})
					.incr();
				throw new Error('Tenant ID not available');
			}

			const env = request.executionRuntime.getEnvironmentType();

			let templateXflowReminderTapTrait: Awaited<
				ReturnType<typeof getTemplateXflowReminderTapTrait>
			> = { tapReminderTrait: undefined, tapReminderTraitState: 'absent' };
			if (isTemplateXflowMessage) {
				const userId = orchestrationContext.recipient?.userId;
				const cachedTapClient = request.restApiClients?.cachedTapClient;

				if (userId && cachedTapClient) {
					try {
						templateXflowReminderTapTrait = await getTemplateXflowReminderTapTrait({
							userId,
							siteId: tenantId,
							tapProvider: new TapProvider(request.metrics, logger, cachedTapClient),
							logger,
						});
					} catch (error) {
						logger.error(
							{ error, messageInstanceId, messageTemplateId, triggerId },
							'Template xflow TAP traits unavailable',
						);
						request.metrics
							.counter(TEMPLATE_XFLOW_HYDRATION_FAILURE_METRIC, {
								reason: TEMPLATE_XFLOW_HYDRATION_FAILURE_REASONS.TAP_TRAITS_UNAVAILABLE,
							})
							.incr();
					}
				}
			}

			const commonReturn = {
				...message.context,
				...(templateXflowReminderTapTrait.tapReminderTrait && {
					tapReminderTrait: templateXflowReminderTapTrait.tapReminderTrait,
				}),
				...(message.context.triggerType
					? { context: { triggerType: message.context.triggerType } }
					: {}),
				targetTenantId,
				collectionEntitlementId,
				messageInstanceId,
				locale,
			};

			const redirectTo = 'jira' as const;
			const onboardingUrl = getLoomOnboardingUrl(env);

			request.metrics
				.counter('twc-sidenav-banner.conf.success.count', { bannerType: 'template-xflow' })
				.incr();

			return { ...commonReturn, redirectTo, onboardingUrl };
		},
	},
}));

type TemplateXflowRenderAbortReason =
	(typeof TEMPLATE_XFLOW_RENDER_ABORT_REASONS)[keyof typeof TEMPLATE_XFLOW_RENDER_ABORT_REASONS];

type TemplateXflowRenderLifecycle = {
	start: () => void;
	complete: () => void;
	fail: (error: Error) => void;
	abort: (reason: TemplateXflowRenderAbortReason) => void;
};

const templateXflowRenderMetadata = {
	messageTemplateId: TEMPLATE_XFLOW_MESSAGE_TEMPLATE_ID,
	placementId: TEMPLATE_XFLOW_PLACEMENT_ID,
	owner: 'growth-starburst',
};

const useTemplateXflowRenderLifecycle = (): TemplateXflowRenderLifecycle => {
	const experienceRef = useRef<UFOExperience | null>(null);
	const stateRef = useRef<'idle' | 'running' | 'terminal'>('idle');

	const start = useCallback(() => {
		if (stateRef.current !== 'idle') {
			return;
		}

		const experience = new UFOExperience(TEMPLATE_XFLOW_RENDER_EXPERIENCE, {
			performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
			type: ExperienceTypes.Load,
		});
		experienceRef.current = experience;
		stateRef.current = 'running';
		void experience.start();
	}, []);

	const complete = useCallback(() => {
		if (stateRef.current !== 'running' || !experienceRef.current) {
			return;
		}

		stateRef.current = 'terminal';
		void experienceRef.current.success({ metadata: templateXflowRenderMetadata });
	}, []);

	const fail = useCallback((_error: Error) => {
		if (stateRef.current !== 'running' || !experienceRef.current) {
			return;
		}

		stateRef.current = 'terminal';
		void experienceRef.current.failure({
			metadata: {
				...templateXflowRenderMetadata,
				reason: TEMPLATE_XFLOW_RENDER_FAILURE_REASON,
			},
		});
	}, []);

	const abort = useCallback((reason: TemplateXflowRenderAbortReason) => {
		if (stateRef.current !== 'running' || !experienceRef.current) {
			return;
		}

		stateRef.current = 'terminal';
		void experienceRef.current.abort({
			metadata: { ...templateXflowRenderMetadata, reason },
		});
	}, []);

	useEffect(
		() => () => {
			abort(TEMPLATE_XFLOW_RENDER_ABORT_REASONS.COMPONENT_UNMOUNTED);
		},
		[abort],
	);

	return useMemo(() => ({ start, complete, fail, abort }), [abort, complete, fail, start]);
};

const TemplateXflowRenderStart = ({
	renderLifecycle,
	children,
}: {
	renderLifecycle: TemplateXflowRenderLifecycle;
	children: React.ReactNode;
}) => {
	const [hasStarted, setHasStarted] = useState(false);

	useEffect(() => {
		renderLifecycle.start();
		setHasStarted(true);
	}, [renderLifecycle]);

	useEffect(
		() => () => {
			renderLifecycle.abort(TEMPLATE_XFLOW_RENDER_ABORT_REASONS.DELIVERY_CANCELLED);
		},
		[renderLifecycle],
	);

	return hasStarted ? children : null;
};

const TemplateXflowBannerContent = ({
	heading,
	blueprintModuleCompleteKey,
	...props
}: MessageFrontendProps & {
	heading: string;
	blueprintModuleCompleteKey: string;
}) => {
	const { formatMessage } = useIntl();
	const { templateXflow } = useContext(ConfluenceExperimentsContext);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageDismissed, messageSnoozed } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const reminderState = props.tapReminderTrait ?? templateXflow?.reminderState ?? 'absent';
	const additionalMetadataFromFrontend = {
		promptSurface: 'sideNavBanner',
		blueprintModuleCompleteKey,
		experimentKey: TEMPLATE_XFLOW_EXPERIMENT_KEY,
		messageTemplateId: TEMPLATE_XFLOW_MESSAGE_TEMPLATE_ID,
		placementId: TEMPLATE_XFLOW_PLACEMENT_ID,
		reminderState,
	};
	const fireTemplateXflowReminderChanged = (
		reminderState: 'remind' | 'dismissed',
		sourceAction: 'remindMeLater' | 'tryItFree' | 'explicitDismiss',
	) => {
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'changed',
			actionSubject: 'twcTemplateXflowReminder',
			attributes: {
				blueprintModuleCompleteKey,
				experimentKey: TEMPLATE_XFLOW_EXPERIMENT_KEY,
				reminderState,
				source: 'sideNavBanner',
				sourceAction,
			},
		});
	};

	const handleDismiss = () => {
		templateXflow?.dismissForSession();
		fireTemplateXflowReminderChanged('dismissed', 'explicitDismiss');
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112Dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'dismiss' },
			additionalMetadataFromFrontend,
		});
		void messageDismissed();
		props.handleOnDismissed();
	};

	const handleTryItFree = (clickEvent: MouseEvent<HTMLElement>) => {
		clickEvent.preventDefault();
		templateXflow?.dismissForSession();
		fireTemplateXflowReminderChanged('dismissed', 'tryItFree');
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112TryItFree',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: { actionType: 'open' },
			additionalMetadataFromFrontend,
		});
		templateXflow?.openTwcXflowModal?.('confluence-xflow-sidenav');
		void messageDismissed();
		props.handleOnDismissed();
	};

	const handleRemindMeLater = () => {
		fireTemplateXflowReminderChanged('remind', 'remindMeLater');
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112RemindMeLater',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'snooze', snoozeDuration: 1 },
			additionalMetadataFromFrontend,
		});
		void messageSnoozed(1);
		props.handleOnDismissed();
	};

	return (
		<PersonalizedBanner
			heading={heading}
			locale={props.locale}
			recommendedApp="loom"
			onClose={handleDismiss}
			onCtaClick={handleTryItFree}
			ctaLabel={formatMessage(templateXflowMessages.tryItFree)}
			secondaryActionLabel={formatMessage(templateXflowMessages.remindMeLater)}
			onSecondaryAction={handleRemindMeLater}
			href={props.onboardingUrl}
			messageInstanceId={props.messageInstanceId}
			experimentKey={props.experimentKey}
			actionTier={props.actionTier}
			targetTenantId={props.targetTenantId}
			collectionEntitlementId={props.collectionEntitlementId}
			surfaceProduct="confluence"
			blueprintModuleCompleteKey={blueprintModuleCompleteKey}
			messageTemplateId={TEMPLATE_XFLOW_MESSAGE_TEMPLATE_ID}
			placementId={TEMPLATE_XFLOW_PLACEMENT_ID}
			promptSurface="sideNavBanner"
			reminderState={reminderState}
		/>
	);
};

const TemplateXflowBannerCore = ({
	renderLifecycle,
	...props
}: MessageFrontendProps &
	PlacementErrorBoundaryProps & { renderLifecycle: TemplateXflowRenderLifecycle }) => {
	const { tapReminderTrait, waitForTrigger } = props;
	const { formatMessage } = useIntl();
	const { isActive: isTemplateViewTriggerActive } = useTrigger(TEMPLATE_VIEW_TRIGGER_ID);
	const { isActive: isTemplateAppliedTriggerActive } = useTrigger(TEMPLATE_APPLIED_TRIGGER_ID);
	const { fireTrigger: fireTemplateViewTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_VIEW_TRIGGER_ID,
	});
	const { fireTrigger: fireTemplateAppliedTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_APPLIED_TRIGGER_ID,
	});
	const { templateXflow } = useContext(ConfluenceExperimentsContext);
	const currentPageTemplateData = templateXflow?.currentPageTemplateData;
	const blueprintModuleCompleteKey = currentPageTemplateData?.blueprintModuleCompleteKey;
	const templateHeadingDescriptor = blueprintModuleCompleteKey
		? templateXflowHeadingByBlueprintModuleCompleteKey[blueprintModuleCompleteKey]
		: undefined;
	const templateHeading = templateHeadingDescriptor
		? formatMessage(templateHeadingDescriptor)
		: undefined;
	const isTemplateXflowCohortEligible =
		currentPageTemplateData?.isEligible === true || tapReminderTrait === 'remind';
	const isTemplateXflowDismissed =
		tapReminderTrait === 'dismissed' ||
		templateXflow?.reminderState === 'dismissed' ||
		templateXflow?.isTerminallyDismissed === true;
	const isTemplateTriggerActive =
		Boolean(isTemplateViewTriggerActive) || Boolean(isTemplateAppliedTriggerActive);
	const isTemplateXflowGateEnabled = fg(TEMPLATE_XFLOW_GATE);
	const shouldStartTemplateXflowDelay =
		isTemplateXflowGateEnabled &&
		!isTemplateXflowDismissed &&
		isTemplateXflowCohortEligible &&
		isTemplateTriggerActive &&
		Boolean(currentPageTemplateData?.contentId) &&
		Boolean(templateHeadingDescriptor);
	const [hasTemplateXflowDelayElapsed, setHasTemplateXflowDelayElapsed] = useState(false);

	useEffect(() => {
		setHasTemplateXflowDelayElapsed(false);

		if (!shouldStartTemplateXflowDelay) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setHasTemplateXflowDelayElapsed(true);
		}, TEMPLATE_XFLOW_DELAY_MS);

		return () => clearTimeout(timeoutId);
	}, [
		blueprintModuleCompleteKey,
		currentPageTemplateData?.contentId,
		shouldStartTemplateXflowDelay,
	]);

	const resetTemplateTriggers = useCallback(() => {
		renderLifecycle.abort(TEMPLATE_XFLOW_RENDER_ABORT_REASONS.POST_OFFICE_TEARDOWN);
		fireTemplateViewTrigger({ isActive: false });
		fireTemplateAppliedTrigger({ isActive: false });
	}, [fireTemplateAppliedTrigger, fireTemplateViewTrigger, renderLifecycle]);

	if (
		isTemplateXflowDismissed ||
		!templateHeading ||
		!blueprintModuleCompleteKey ||
		!waitForTrigger
	) {
		return null;
	}

	return waitForTrigger({
		isTriggerActive: hasTemplateXflowDelayElapsed,
		messageContent: (
			<TemplateXflowRenderStart renderLifecycle={renderLifecycle}>
				<TrackMount onMount={renderLifecycle.complete}>
					<TemplateXflowBannerContent
						{...props}
						heading={templateHeading}
						blueprintModuleCompleteKey={blueprintModuleCompleteKey}
					/>
				</TrackMount>
			</TemplateXflowRenderStart>
		),
		onTeardown: resetTemplateTriggers,
	});
};

const TemplateXflowBannerWithErrorBoundary = withErrorBoundary(TemplateXflowBannerCore);

const TemplateXflowBanner = (props: MessageFrontendProps) => {
	const renderLifecycle = useTemplateXflowRenderLifecycle();

	return (
		<TemplateXflowBannerWithErrorBoundary
			{...props}
			renderLifecycle={renderLifecycle}
			errorBoundaryLocation={TEMPLATE_XFLOW_RENDER_EXPERIENCE}
			placementId={TEMPLATE_XFLOW_PLACEMENT_ID}
			messageTemplateId={TEMPLATE_XFLOW_MESSAGE_TEMPLATE_ID}
			onError={renderLifecycle.fail}
			fallbackRender={() => null}
		/>
	);
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: (props: MessageFrontendProps) => {
			const { experimentKey, explicitEnableRequired } = props;
			const isTemplateXflowMessage =
				experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY && explicitEnableRequired === true;

			if (isTemplateXflowMessage) {
				return <TemplateXflowBanner {...props} />;
			}

			return null;
		},
	},
});
